import { getData, postData, updateData, deleteData } from './request'
import { getAPIBaseURL } from './helpers'

/**
 * List all active offers
 */
export const listOffers = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams()
    if (params.status) queryParams.append('status', params.status)
    if (params.centerId) queryParams.append('centerId', params.centerId)
    if (params.category) queryParams.append('category', params.category)
    if (params.search) queryParams.append('search', params.search)
    if (params.tags) queryParams.append('tags', params.tags)
    if (params.country) queryParams.append('country', params.country)
    if (params.city) queryParams.append('city', params.city)
    if (params.nearby === true) queryParams.append('nearby', 'true')
    if (params.oneTableLeft === true) queryParams.append('oneTableLeft', 'true')
    if (params.booked === true) queryParams.append('booked', 'true')
    if (params.limit) queryParams.append('limit', params.limit)
    if (params.skip) queryParams.append('skip', params.skip)

    const queryString = queryParams.toString()
    const url = `${getAPIBaseURL()}/v1/offers${queryString ? '?' + queryString : ''}`
    
    const response = await getData(url, {}, {})
    if (response.error) {
      return { status: response.result?.status || 500, data: null }
    }
    return { status: 200, data: response.result.data.data }
  } catch (error) {
    console.error('Error listing offers:', error)
    return { status: error.response?.status || 500, data: null }
  }
}

/**
 * Get offer by ID
 */
export const getOffer = async (offerId) => {
  try {
    const url = `${getAPIBaseURL()}/v1/offers/${offerId}`
    const response = await getData(url, {}, {})
    if (response.error) {
      return { status: response.result?.status || 500, data: null }
    }
    return { status: 200, data: response.result.data.data }
  } catch (error) {
    console.error('Error getting offer:', error)
    return { status: error.response?.status || 500, data: null }
  }
}

/**
 * Create offer (CENTER only)
 */
export const createOffer = async (offerData) => {
  try {
    const url = `${getAPIBaseURL()}/v1/offers`
    const token = localStorage.getItem('token')
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { 'token': token } : {})
    }
    const response = await postData(url, headers, offerData)
    if (response.error) {
      return { status: response.result?.status || 500, error: response.result?.data?.data || 'Failed to create offer' }
    }
    return { status: 201, data: response.result.data.data }
  } catch (error) {
    console.error('Error creating offer:', error)
    return { status: error.response?.status || 500, error: error.response?.data?.data || 'Failed to create offer' }
  }
}

/**
 * Update offer (CENTER only)
 */
export const updateOffer = async (offerId, offerData) => {
  try {
    const url = `${getAPIBaseURL()}/v1/offers`
    const token = localStorage.getItem('token')
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { 'token': token } : {})
    }
    // Include offerId in the request body
    const payload = { ...offerData, offerId }
    const response = await updateData(url, headers, payload)
    if (response.error) {
      return { status: response.result?.status || 500, error: response.result?.data?.data || 'Failed to update offer' }
    }
    return { status: 200, data: response.result.data.data }
  } catch (error) {
    console.error('Error updating offer:', error)
    return { status: error.response?.status || 500, error: error.response?.data?.data || 'Failed to update offer' }
  }
}

/**
 * Publish offer (CENTER only)
 */
export const publishOffer = async (offerId) => {
  try {
    const url = `${getAPIBaseURL()}/v1/offers/publish`
    const token = localStorage.getItem('token')
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { 'token': token } : {})
    }
    const response = await postData(url, headers, { offerId })
    if (response.error) {
      return { status: response.result?.status || 500, error: response.result?.data?.data || 'Failed to publish offer' }
    }
    return { status: 200, data: response.result.data.data }
  } catch (error) {
    console.error('Error publishing offer:', error)
    return { status: error.response?.status || 500, error: error.response?.data?.data || 'Failed to publish offer' }
  }
}

/**
 * Delete offer (CENTER only)
 */
export const deleteOffer = async (offerId) => {
  try {
    const url = `${getAPIBaseURL()}/v1/offers/${offerId}`
    const token = localStorage.getItem('token')
    const headers = token ? { 'token': token } : {}
    const response = await deleteData(url, headers, {})
    if (response.error) {
      return { status: response.result?.status || 500, error: response.result?.data?.data || 'Failed to delete offer' }
    }
    return { status: 200, data: response.result.data.data }
  } catch (error) {
    console.error('Error deleting offer:', error)
    return { status: error.response?.status || 500, error: error.response?.data?.data || 'Failed to delete offer' }
  }
}

/**
 * Get center's own offers (CENTER only)
 */
export const getCenterOffers = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams()
    if (params.status) queryParams.append('status', params.status)
    if (params.limit) queryParams.append('limit', params.limit)
    if (params.skip) queryParams.append('skip', params.skip)

    const queryString = queryParams.toString()
    const url = `${getAPIBaseURL()}/v1/center/offers${queryString ? '?' + queryString : ''}`
    
    const token = localStorage.getItem('token')
    const headers = token ? { 'token': token } : {}
    
    const response = await getData(url, headers, {})
    if (response.error) {
      return { status: response.result?.status || 500, data: null }
    }
    return { status: 200, data: response.result.data.data }
  } catch (error) {
    console.error('Error getting center offers:', error)
    return { status: error.response?.status || 500, data: null }
  }
}

/**
 * Initialize offer order (ATTENDEE)
 */
export const initOfferOrder = async (offerId, quantity) => {
  try {
    const url = `${getAPIBaseURL()}/v1/offers/${offerId}/orders`
    const token = localStorage.getItem('token')
    const headers = token ? { 'token': token } : {}
    
    const formData = new URLSearchParams()
    formData.append('offerId', offerId)
    formData.append('quantity', quantity.toString())
    formData.append('fees', '0')
    
    const response = await postData(url, headers, formData.toString())
    if (response.error) {
      const errorData = response.result?.data
      const errorMessage = errorData?.data || errorData?.message || errorData || 'Failed to initialize order'
      return { status: response.result?.status || 500, error: errorMessage }
    }
    return { status: response.result.status, data: response.result.data.data }
  } catch (error) {
    console.error('Error initializing offer order:', error)
    return { status: error.response?.status || 500, error: 'Failed to initialize order' }
  }
}

/**
 * Initialize offer payment (ATTENDEE)
 */
export const initializeOfferPayment = async (orderId, redirectUrl) => {
  try {
    const url = `${getAPIBaseURL()}/v1/offers/payments/initialize`
    const token = localStorage.getItem('token')
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { 'token': token } : {})
    }
    
    const payload = {
      orderId,
      redirectUrl
    }
    
    const response = await postData(url, headers, JSON.stringify(payload))
    if (response.error) {
      return { status: response.result?.status || 500, error: response.result?.data?.data || 'Failed to initialize payment' }
    }
    return { status: response.result.status, data: response.result.data.data }
  } catch (error) {
    console.error('Error initializing offer payment:', error)
    return { status: error.response?.status || 500, error: 'Failed to initialize payment' }
  }
}

/**
 * List offer stories (offers created in last 24 hours)
 */
export const listOfferStories = async () => {
  try {
    const url = `${getAPIBaseURL()}/v1/offers/stories`
    const response = await getData(url, {}, {})
    if (response.error) {
      return { status: response.result?.status || 500, data: null }
    }
    return { status: 200, data: response.result.data.data }
  } catch (error) {
    console.error('Error listing offer stories:', error)
    return { status: error.response?.status || 500, data: null }
  }
}

/**
 * List all offers for admin
 */
export const listAdminOffers = async () => {
  const url = `${getAPIBaseURL()}/v1/admin/offers`
  const token = localStorage.getItem('token')
  const headers = token ? { 'token': token } : {}
  const res = await getData(url, headers, {})
  if (res.error === '') {
    return { status: res.result.status, data: res.result.data.data || res.result.data }
  }
  return { status: res.result?.status || 500, error: res.result?.data?.data || res.result?.data?.message }
}

